<template>
  <div class="compatibility-informations-wrapper">
    <div class="compatibility-activity-wrapper" :style="gridStyle">
      <div
        v-for="column in columns"
        :key="column"
        class="compatibility-attribute"
        :class="{ 'compatibility-status-column': statusColumns.includes(column) }"
      >
        <template v-if="column === 'plate'">
          {{ licencePlateDisplayValue }}
        </template>
        <template v-else-if="column === 'vin'">
          {{ props.compat.vin || '-' }}
        </template>
        <template v-else-if="column === 'brand'">
          <v-select
            v-if="showBrandSelect"
            v-model="selectedBrand"
            :items="brands"
            :label="$t('checker.legends.brand')"
            dense
            dark
            :outlined="true"
            class="resolver-select"
            hide-details
            @change="onBrandChange"
          />
          <span v-else>{{ brandDisplayValue }}</span>
        </template>
        <template v-else-if="column === 'model'">
          <v-select
            v-if="showModelSelect"
            v-model="selectedModel"
            :items="models"
            :label="$t('checker.legends.model')"
            dense
            dark
            :outlined="true"
            class="resolver-select"
            hide-details
            :disabled="!selectedBrand"
            @change="onModelChange"
          />
          <span v-else>{{ modelDisplayValue }}</span>
        </template>
        <template v-else-if="column === 'reg'">
          <DatePicker
            v-if="isEditingVehicleInfo"
            v-model="registrationDate"
            class="vehicle-info-date-picker"
            mode="month"
            format="MM/YYYY"
            :showToday="false"
            :open="registrationPickerOpen"
            @openChange="registrationPickerOpen = $event"
            @panelChange="handleRegistrationChange"
          />
          <span v-else>{{ registrationDateDisplayValue }}</span>
        </template>
        <template v-else-if="column === 'calibration'">
          <DatePicker
            v-if="isEditingVehicleInfo"
            v-model="calibrationDate"
            class="vehicle-info-date-picker"
            mode="month"
            format="MM/YYYY"
            :showToday="false"
            :open="calibrationPickerOpen"
            @openChange="calibrationPickerOpen = $event"
            @panelChange="handleCalibrationChange"
          />
          <span v-else>{{ calibrationDateDisplayValue }}</span>
        </template>
        <template v-else-if="column === 'tachograph'">
          {{ (props.compat.dtcoData && props.compat.dtcoData.manufacturerCode) || '-' }}
        </template>
        <template v-else-if="column === 'firmware'">
          {{ (props.compat.dtcoData && props.compat.dtcoData.firmwareVersion) || '-' }}
        </template>
        <template v-else-if="column === 'generation'">
          {{ dddGeneration }}
        </template>
        <template v-else-if="column === 'eets'">
          <v-tooltip v-if="requiresSoftwareUpdate" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="orange"
                v-bind="attrs"
                v-on="on"
              >
                {{ icons.download }}
              </v-icon>
            </template>
            <div>{{ $t('checker.eets_software_update_required') }}</div>
            <div v-if="props.tableType === 'ddd'">
              {{ $t('checker.vu_serial_number') }}: {{ vuSerialNumber }}
            </div>
            <div v-if="props.tableType === 'ddd' && rcmSerialNumber">
              {{ $t('checker.rcm_serial_number') }}: {{ rcmSerialNumber }}
            </div>
          </v-tooltip>
          <v-tooltip v-else-if="showDddEetsTooltip" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                :color="getStatusColor(eetsTollingValue)"
                v-bind="attrs"
                v-on="on"
              >
                {{ getStatusIcon(eetsTollingValue) }}
              </v-icon>
            </template>
            <div>{{ $t('checker.vu_serial_number') }}: {{ vuSerialNumber }}</div>
            <div v-if="rcmSerialNumber">
              {{ $t('checker.rcm_serial_number') }}: {{ rcmSerialNumber }}
            </div>
          </v-tooltip>
          <v-icon v-else :color="getStatusColor(eetsTollingValue)">
            {{ getStatusIcon(eetsTollingValue) }}
          </v-icon>
        </template>
        <v-icon v-else-if="column === 'gnss'" :color="getStatusColor(polandETollValue)">
          {{ getStatusIcon(polandETollValue) }}
        </v-icon>
        <v-icon v-else-if="column === 'live'" :color="getStatusColor(liveTrackingValue)">
          {{ getStatusIcon(liveTrackingValue) }}
        </v-icon>
        <v-icon v-else-if="column === 'remote'" :color="getStatusColor(remoteDownloadValue)">
          {{ getStatusIcon(remoteDownloadValue) }}
        </v-icon>
        <v-btn
          v-else-if="column === 'action' && !isEditingVehicleInfo"
          class="vehicle-info-check-button ctp-btn"
          dark
          rounded
          small
          :disabled="isUpdating"
          @click="isEditingVehicleInfo = true"
        >
          {{ $t('checker.edit') }}
        </v-btn>
        <v-btn
          v-else-if="column === 'action'"
          class="vehicle-info-check-button ctp-btn"
          dark
          rounded
          small
          :loading="isUpdating"
          :disabled="!selectedBrand || !selectedModel || !registrationDate || isUpdating"
          @click="resubmitVehicleInfo"
        >
          {{ $t('checker.check_again') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, PropType } from 'vue';
import { CompatibilityResponse } from 'ctp-compatibility-checker';
import { DatePicker } from 'ant-design-vue';
import moment from 'moment';
import { VehicleInfoRequest } from '../vehicleInfo';
import { brandModels, brands } from '../constants/brandModels';
import getDddGeneration from '../dddGeneration';
import { requiresEetsSoftwareUpdate } from '../compatibilityNotifications';
import {
  compatibilityStatusColumns,
  CompatibilityColumn,
  CompatibilityTableType
} from './tableColumns';
import './styles/compatibility-information.scss';

const icons = {
  check: 'mdi-check',
  close: 'mdi-close',
  alert: 'mdi-alert',
  download: 'mdi-download'
};
const statusColumns = compatibilityStatusColumns;

const getIcon = (value: boolean) => (value ? icons.check : icons.close);

// Fields whose value is null/undefined (not returned by the API) show a
// distinct "unknown" alert icon instead of the regular check/close icons,
// so users can tell "not compatible" apart from "no data available".
const getStatusIcon = (value: boolean | null | undefined) => {
  if (value === null || value === undefined) {
    return icons.alert;
  }

  return getIcon(value);
};

const getStatusColor = (value: boolean | null | undefined) => {
  if (value === null || value === undefined) {
    return 'orange';
  }

  return value ? 'green' : 'red';
};

const props = defineProps({
  compat: {
    type: Object as PropType<CompatibilityResponse>,
    default: {} as CompatibilityResponse
  },
  request: {
    type: Object as PropType<VehicleInfoRequest | undefined>,
    default: undefined
  },
  rowIndex: {
    type: Number,
    required: true
  },
  columns: {
    type: Array as PropType<CompatibilityColumn[]>,
    required: true
  },
  gridStyle: {
    type: Object as PropType<Record<string, string>>,
    required: true
  },
  tableType: {
    type: String as PropType<CompatibilityTableType>,
    required: true
  },
  isUpdating: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['resolveBrandModel', 'resubmitVehicleInfo']);

const registrationDate = ref<moment.Moment | null>(null);
const calibrationDate = ref<moment.Moment | null>(null);
const registrationPickerOpen = ref(false);
const calibrationPickerOpen = ref(false);
const isEditingVehicleInfo = ref(false);
const selectedBrand = ref<string | null>(null);
const selectedModel = ref<string | null>(null);
const brandDisplayValue = computed(() => props.compat.brand || props.request?.brand || '-');
const modelDisplayValue = computed(() => props.compat.model || props.request?.model || '-');
const registrationDateDisplayValue = computed(() => (
  props.request?.registrationDate ? moment.unix(props.request.registrationDate).format('MM/YYYY') : '-'
));
const calibrationDateDisplayValue = computed(() => (
  props.request?.lastCalibration ? moment.unix(props.request.lastCalibration).format('MM/YYYY') : '-'
));

watch(() => props.request, (request) => {
  registrationDate.value = request?.registrationDate
    ? moment.unix(request.registrationDate)
    : null;
  calibrationDate.value = request?.lastCalibration
    ? moment.unix(request.lastCalibration)
    : null;
  selectedBrand.value = request?.brand || props.compat.brand || null;
  selectedModel.value = request?.model || props.compat.model || null;
  isEditingVehicleInfo.value = false;
}, { immediate: true });

const handleRegistrationChange = (value: moment.Moment | null) => {
  registrationDate.value = value;
  registrationPickerOpen.value = false;
};

const handleCalibrationChange = (value: moment.Moment | null) => {
  calibrationDate.value = value;
  calibrationPickerOpen.value = false;
};

const resubmitVehicleInfo = () => {
  if (!props.request || !selectedBrand.value || !selectedModel.value || !registrationDate.value) {
    return;
  }

  emit('resubmitVehicleInfo', {
    rowIndex: props.rowIndex,
    request: {
      ...props.request,
      brand: selectedBrand.value,
      model: selectedModel.value,
      registrationDate: registrationDate.value.unix(),
      lastCalibration: calibrationDate.value ? calibrationDate.value.unix() : null
    }
  });
};

const eetsTollingValue = computed(() => {
  if (props.compat.eetsTolling === null || props.compat.eetsTolling === undefined) {
    return null;
  }

  return props.compat.eetsTolling;
});

const polandETollValue = computed(() => (
  props.compat.polandEToll === null || props.compat.polandEToll === undefined
    ? null
    : props.compat.polandEToll
));

const liveTrackingValue = computed(() => (
  props.compat.liveTracking === null || props.compat.liveTracking === undefined
    ? null
    : props.compat.liveTracking
));

const remoteDownloadValue = computed(() => (
  props.compat.remoteDownload === null || props.compat.remoteDownload === undefined
    ? null
    : props.compat.remoteDownload
));

const dddGeneration = computed(() => getDddGeneration(props.compat.compatibleNotifications));
const vuSerialNumber = computed(() => props.compat.dtcoData?.vuSerialNumber || '-');
const rcmSerialNumber = computed(() => props.compat.dtcoData?.rcmSerialNumber || '');
const requiresSoftwareUpdate = computed(() => requiresEetsSoftwareUpdate(props.compat));
const showDddEetsTooltip = computed(() => (
  props.tableType === 'ddd'
  && eetsTollingValue.value !== null
  && eetsTollingValue.value !== undefined
));

const licencePlateDisplayValue = computed(() => {
  const licencePlate = props.compat.licencePlateNumber;

  if (!licencePlate || licencePlate.includes('?')) {
    return '-';
  }

  return licencePlate;
});

const isValueMissing = (value?: string | null) => !value || value.trim() === '-' || value.trim().length === 0;

// `eetsTolling` is empty (null/undefined) in 2 cases:
// 1. We have a valid DTCO reading (firmwareVersion is present) but the brand/model
//    couldn't be resolved from it — this is when we ask the user to resolve it manually.
// 2. We simply found no data at all for this vehicle (firmwareVersion is also empty) —
//    in this case there is nothing to resolve, so we should NOT prompt for brand/model.
const hasValidDtcoResponse = computed(
  () => !isValueMissing(props.compat.dtcoData?.firmwareVersion)
);

const showBrandResolver = computed(
  () => props.tableType !== 'vehicleInfo'
    && eetsTollingValue.value === null
    && hasValidDtcoResponse.value
    && isValueMissing(props.compat.brand)
);
const showModelResolver = computed(
  () => props.tableType !== 'vehicleInfo'
    && eetsTollingValue.value === null
    && hasValidDtcoResponse.value
    && isValueMissing(props.compat.model)
);
const showBrandSelect = computed(() => (
  (props.tableType === 'vehicleInfo' && isEditingVehicleInfo.value) || showBrandResolver.value
));
const showModelSelect = computed(() => (
  (props.tableType === 'vehicleInfo' && isEditingVehicleInfo.value) || showModelResolver.value
));

const models = computed(() => {
  if (!selectedBrand.value) {
    return [];
  }

  return brandModels[selectedBrand.value] || [];
});

const onBrandChange = (value: string | null) => {
  selectedBrand.value = value;
  selectedModel.value = null;
};

const onModelChange = (value: string | null) => {
  selectedModel.value = value;

  if (props.tableType !== 'vehicleInfo' && selectedBrand.value && value) {
    emit('resolveBrandModel', {
      rowIndex: props.rowIndex,
      brand: selectedBrand.value,
      model: value,
      compat: props.compat
    });
  }
};
</script>
